'use strict'

import UAParser from "ua-parser-js";
import GlobalHelper from "../common/GlobalHelper.jsx";
import TotalQuantityHelper from '../common/pricing/TotalQuantityHelper';
import { UNIT_GROUP_TYPES } from '../state/pricing/UnitGroupTypes';

class ProposalMergeFields {
    static formatMoney(variables, value, allowZero = false) {
        if (isNaN(value) || (!value && !allowZero)) {
            return ''
        }
        return new Intl.NumberFormat(variables.global.language, {
            style: 'currency',
            currency: variables.quote.currency
        }).format(value)
    }

    static formatPercent(variables, value) {
        if (!value) {
            return ''
        }
        return new Intl.NumberFormat(variables.global.language, { style: 'percent' }).format(value)
    }

    static formatDate(variables, value) {
        if (!value) {
            return ''
        }
        const options = { year: "numeric", month: "long", day: "numeric", timeZone: this.getTimeZone(variables) };
        return new Intl.DateTimeFormat(variables.global.language, options).format(new Date(value))
    }

    static getTimeZone(variables) {
        const parser = new UAParser()
        const ua = window.navigator.userAgent;
        parser.setUA(ua)
        const userAgent = parser.getResult()
        const msie = ua.indexOf("MSIE ");
        const trident = ua.indexOf('Trident/');

        if (msie > 0 || trident > 0) {
            return undefined
        }
        if (parseInt(userAgent.browser.major) < 52 && userAgent.browser.name === 'Firefox') {
            return undefined
        }
        if (parseInt(userAgent.browser.major) < 24 && userAgent.browser.name === 'Chrome') {
            return undefined
        }
        if (parseInt(userAgent.browser.major) < 10 && userAgent.browser.name === 'Safari') {
            return undefined
        }
        if (userAgent.browser.name === 'Edge')
            return undefined
        return variables.global.timeZone
    }

    static includeDefinition(definition, objectType) {
        if (!definition.attributes.availableFrom) {
            return true
        }

        if (objectType === 'price-lists') {
            return definition.attributes.availableFrom.includes('quotes') ||
                definition.attributes.availableFrom.includes('invoices')
        }

        return definition.attributes.availableFrom.includes(objectType) || !objectType
    }

    static includeDefinitionInContact(definition) {
        if (!definition.attributes.availableFrom) {
            return true
        }

        return definition.attributes.availableFrom.includes("persons");
    }

    static formatGroupedQuantity(totals, quantityType, intl) {
        let total = totals[0] // grouped quantities were only calculated for one-time billing method
        if (!total) {
            return ""
        }

        let groupedQuantity = total.attributes.groupedQuantities
            .find(g => g.attributes.groupedQuantityType === quantityType)
        if (!groupedQuantity) {
            return "" 
        }

        const labelAndTotalQuantityValue = TotalQuantityHelper.getTotalQuantityLabelAndAmount(intl, groupedQuantity)

        switch (quantityType) {
            case UNIT_GROUP_TYPES.UNIT:
            case UNIT_GROUP_TYPES.TIME:
                return labelAndTotalQuantityValue[1]
            default:
                return ""
        }
    }

    static mergePricingTableLine(content, item, isDummy) {
        if (isDummy) {
            return content
        }
        const values = {
            '{line_tax_amount}': this.formatMoney(item.variables, item.totalTaxAmount, true),
            '{line_subtotal_amount}': this.formatMoney(item.variables, item.subtotalAmount, true),
            '{line_subtotal_inclusive_amount}': this.formatMoney(item.variables, item.subtotalAmountInclusive, true),
            '{line_subtotal_amount_with_discount}': this.formatMoney(item.variables, item.subtotalAmountWithDiscount, true),
            '{line_subtotal_amount_with_global_discount}': this.formatMoney(item.variables, item.subtotalAmountWithGlobalDiscount, true),
            '{line_total_amount}': this.formatMoney(item.variables, item.totalAmount, true),
            '{line_total_discount_amount}': this.formatMoney(item.variables, item.discountFullAmount, true),
            '{line_total_discount_amount_percentage}': item.discountFullAmountPercentage ? Math.round(item.discountFullAmountPercentage * 100) + '%' : '',
            '{line_unit_price}': !item.hidePrice ? this.formatMoney(item.variables, item.unitPrice, true) : '',
            '{line_unit_price_after_discount}': !item.hidePrice ? this.formatMoney(item.variables, item.calculatedUnitPrice, true) : '',
            '{line_unit_price_inclusive}': !item.hidePrice ? this.formatMoney(item.variables, item.unitPriceInclusive, true) : '',
            '{line_unit_price_after_discount_tax_exclusive}': item.measure ? this.formatMoney(item.variables, item.subtotalAmount / item.measure, true) : '',
            '{line_item_title}': item.title,
            '{line_item_measure}': item.measure,
            '{line_item_sku}': item.product ? item.product.sku : '',
            '{line_item_manufacturer_sku}': (item.itemObject && item.itemObject.attributes.itemObject) ?
                item.itemObject.attributes.itemObject.attributes.manufacturerSku : '',
            '{line_item_manufacturers}': item.product ? item.product.manufacturers : '',
            '{line_item_short_description}': item.shortDescription,
            '{line_item_long_description}': item.longDescription,
            '{line_item_inventory_requested}': item.inventoryRequestedMeasure,
            '{line_item_inventory_received}': item.inventoryReceivedMeasure,
            '{line_item_inventory_reserved}': item.inventoryReservedMeasure,
            '{line_item_invoiced_measure}': item.invoicedMeasure,
            '{line_item_internal_notes}': item.internalNotes,
            '{line_item_tax_name}': item.taxName,
            '{line_item_total_tax_rate}': item.totalTaxRate ? item.totalTaxRate + '%' : '',
        }
        let mergedContent = content
        for (let key in values) {
            const value = values[key]
            mergedContent = mergedContent.replace(new RegExp(key, 'g'), value)
        }
        return mergedContent
    }

    static merge(content, variables, isDummy, intl) {
        if (isDummy) {
            return content
        }

        const localWithoutRegionCode = variables.global.language.split('-')[0]
        const customer = variables.customer
        const organization = customer.organization || {}
        const address = customer.address || {}
        const shipTo = variables.shipTo || {}
        const shipToAddress = shipTo.address || {}
        const shipToOrganization = shipTo.organization || {}
        const values = {
            '{first_name}': customer.firstName,
            '{last_name}': customer.lastName,
            '{name}': customer.name,
            '{street}': address.street,
            '{city}': address.city,
            '{postal_code}': address.postalCode,
            '{country}': address.country,
            '{state}': address.state,
            '{contact_tax_number}': customer.taxNumber || variables.customerParentOrganization.taxNumber,
            '{account_tax_number}': variables.company.taxNumber,
            '{state_code}': GlobalHelper.getStateCodeFromAddress(address, localWithoutRegionCode),
            '{organization_name}': organization.name,
            '{email}': customer.email,
            '{phone_number}': GlobalHelper.formatPhoneNumber(customer.phoneNumber, address.countryCode),
            '{total_amount}': this.formatMoney(variables, variables.quote.totalAmount),
            '{total_amount_without_tax}': this.formatMoney(variables, variables.quote.totalAmountWithoutTax),
            '{deposit_amount}': this.formatMoney(variables, variables.quote.depositAmount),
            '{deposit_ratio}': this.formatPercent(variables, variables.quote.depositRatio),
            '{remaining_balance}': this.formatMoney(variables, variables.quote ? variables.quote.balance : 0, true),
            '{paid_amount}': this.formatMoney(variables, variables.quote ? (variables.quote.totalAmount - variables.quote.balance) : 0, true),
            '{project_title}': variables.project.title,
            '{grand_total_discount_amount}': this.formatMoney(variables, variables.quote.discount ? variables.quote.discount.grandTotalAmount : 0),
            '{grand_total_discount_ratio}': this.formatPercent(variables, variables.quote.discount ? variables.quote.discount.grandTotalRatio : 0),
            '{discount_amount}': this.formatMoney(variables, variables.quote.discount ? variables.quote.discount.amount : 0),
            '{discount_percent}': this.formatPercent(variables, variables.quote.discount && variables.quote.discount.type === 'percent-before-taxes' ? variables.quote.discount.value / 100 : 0),
            '{due_date}': this.formatDate(variables, variables.document.dueDate),
            '{date}': this.formatDate(variables, variables.document.date),
            '{lightspeed_external_id}': variables.quote.lightspeedExternalId || '',
            '{lightspeed_workorder_id}': variables.quote.lightspeedWorkorderId || '',
            '{reference}': variables.document.reference,
            '{identifier}': variables.document.reference,
            '{document_name}': variables.quote.documentName,
            '{pdf_url}': variables.document.pdfUri,
            '{ship_to_first_name}': shipTo.firstName,
            '{ship_to_last_name}': shipTo.lastName,
            '{ship_to_name}': shipTo.name,
            '{ship_to_street}': shipToAddress.street,
            '{ship_to_city}': shipToAddress.city,
            '{ship_to_postal_code}': shipToAddress.postalCode,
            '{ship_to_country}': shipToAddress.country,
            '{ship_to_state}': shipToAddress.state,
            '{ship_to_state_code}': GlobalHelper.getStateCodeFromAddress(shipToAddress, localWithoutRegionCode),
            '{ship_to_organization_name}': shipToOrganization.name,
            '{ship_to_phone_number}': GlobalHelper.formatPhoneNumber(shipTo.phoneNumber, shipToAddress.countryCode),
            '{seller_name}': variables.seller.name,
            '{seller_email}': variables.seller.email,
            '{company_name}': variables.company.name,
            '{company_phone_number}': GlobalHelper.formatPhoneNumber(variables.company.phoneNumber, address.countryCode),
            '{company_email}': variables.company.email,
            '{company_street}': variables.company.address.street,
            '{company_city}': variables.company.address.city,
            '{company_state}': variables.company.address.state,
            '{company_state_code}': GlobalHelper.getStateCodeFromAddress(variables.company.address, localWithoutRegionCode),
            '{company_country}': variables.company.address.country,
            '{company_postal_code}': variables.company.address.postalCode,
            '{po_number}': variables.quote ? variables.quote.poNumber : '',
            '{expiration_date}': this.formatDate(variables, variables.document.expirationDate),
            '{last_internal_note}': (variables.project.internalNotes && variables.project.internalNotes.length > 0) ? variables.project.internalNotes.slice(-1)[0].attributes.notes : '',
            '{order_reference}': variables.quote.orderReferenceIdentifier ? variables.quote.orderReferenceIdentifier : '',
            '{captured_at}': this.formatDate(variables, variables.document.capturedAt),
            '{grouped_quantity_unit}': variables.quote.totals ? this.formatGroupedQuantity(variables.quote.totals, UNIT_GROUP_TYPES.UNIT, intl) : '',
            '{grouped_quantity_time}': variables.quote.totals ? this.formatGroupedQuantity(variables.quote.totals, UNIT_GROUP_TYPES.TIME, intl) : '',
        }

        for (let index in variables.project.customFieldDefinitions) {
            const def = variables.project.customFieldDefinitions[index]
            const varName = this.formatCustomFieldNameToVariable(def.attributes.name, false)
            const value = variables.project.customFieldValues[def.id]

            if (this.includeDefinition(def, variables.project.documentType)) {
                switch (def.attributes.fieldType) {
                    case 'date':
                        values[varName] = this.formatDate(variables, value)
                        break
                    case 'multi-line-text':
                        values[varName] = value ? value.split('\n').join('<br />') : ''
                        break
                    default:
                        values[varName] = value || ''
                        break
                }
            }


            const varNameContact = this.formatCustomFieldNameToVariable(def.attributes.name, true)
            const valueContact = variables.customer?.id ? variables.customer?.customFieldValues[def.id] : ""

            if (this.includeDefinitionInContact(def)) {
                switch (def.attributes.fieldType) {
                    case 'date':
                        values[varNameContact] = this.formatDate(variables, valueContact)
                        break
                    case 'multi-line-text':
                        values[varNameContact] = valueContact ? valueContact.split('\n').join('<br />') : ''
                        break
                    default:
                        values[varNameContact] = valueContact || ''
                        break
                }
            }
        }

        let mergedContent = content
        for (let key in values) {
            const value = values[key]
            mergedContent = mergedContent.replace(new RegExp(key, 'g'), value)
        }
        return mergedContent
    }

    static formatCustomFieldNameToVariable(name, isContactField) {
        if (isContactField) {
            return "{contact_" + name.replace(/([\' _&~%":*?<>“])+/g, "_").trim().toLowerCase() + "}"
        }
        return "{" + name.replace(/([\' _&~%":*?<>“])+/g, "_").trim().toLowerCase() + "}"
    }
}

export default ProposalMergeFields


// WEBPACK FOOTER //
// ./src/template/MergeFields.jsx